(() => {
  const configElement = document.getElementById('mymara-feature-loader-config');
  if (!configElement) return;

  const config = JSON.parse(configElement.textContent);
  const { assets } = config;
  const WISHLIST_BUTTON_SELECTOR = '[data-wishlist-button]';
  const WISHLIST_TRIGGER_SELECTOR = `${WISHLIST_BUTTON_SELECTOR}, #wishlist-icon-bubble`;
  const WISHLIST_PAGE_SELECTOR = '[data-wishlist-page]';
  const HOMEPAGE_FEATURES_ON_DEMAND = config.homepageFeaturesOnDemand;
  const PREDICTIVE_SEARCH_TRIGGER_SELECTOR = 'drawer-button[data-target="drawer-search"], #drawer-search input[type="search"]';
  const CHAPORT_TRIGGER_SELECTOR = '[data-open-chat]';
  // scroll is not captured: element scrolls (e.g. the auto-scrolling Judge.me medals) don't bubble to window, page scrolls do
  const CHAPORT_INTERACTION_EVENTS = [['pointerdown', true], ['keydown', true], ['scroll', false]];
  const CHAPORT_FALLBACK_DELAY = 15000;
  // Theme settings → Live chat: 'chaport' (default), 'mymara' (MyMara Chat, tag in snippets/mymara-chat.liquid) or 'none'
  const CHAT_WIDGET = ['mymara', 'none'].includes(config.chatWidget) ? config.chatWidget : 'chaport';
  const MYMARA_CHAT_OPEN_EVENT = 'mymara-chat:open';
  const MYMARA_CHAT_READY_EVENT = 'mymara-chat:ready';
  const FREE_SHIPPING_SELECTOR = 'free-shipping-progress-bar';
  const FREE_SHIPPING_DRAWER_SELECTOR = 'cart-drawer';
  const FREE_SHIPPING_DRAWER_TRIGGER_SELECTOR = '#cart-icon-bubble';
  const NEWSLETTER_POPUP_CONFIG_SELECTOR = '[data-newsletter-popup-config]';
  const NEWSLETTER_POPUP_TRIGGER_SELECTOR = '.newsletter-modal__opener button';
  const NEWSLETTER_POPUP_CLOSE_SELECTOR = '.newsletter-modal__opener .newsletter-modal__close';
  const NEWSLETTER_POPUP_INTERACTION_SELECTOR = `${NEWSLETTER_POPUP_TRIGGER_SELECTOR}, ${NEWSLETTER_POPUP_CLOSE_SELECTOR}`;
  const NEWSLETTER_POPUP_CLOSED_KEY = 'newsletterPopupClosed';
  const EU_LOCALIZATION_POPUP_SELECTOR = '[data-eu-localization-modal]';
  const CHAT_LINK_SKIP_SELECTOR = 'a, button, textarea, input, select, option, script, style, noscript, svg, math, [data-open-chat]';
  const CHAT_LINK_PATTERN = /(^|[^A-Za-z0-9_])(chat|vestlusaken|vestlusaknas)(?=$|[^A-Za-z0-9_])/gi;
  const CARD_FEATURE_ROOT_SELECTOR = 'quick-view, card-product-swatch, card-product-sizes';
  const CARD_FEATURE_INTERACTION_SELECTOR = `${CARD_FEATURE_ROOT_SELECTOR}, ${CARD_FEATURE_ROOT_SELECTOR} *`;
  const PRODUCT_RUNTIME_SELECTOR = 'variant-selects, mymara-size-guide';
  let wishlistScriptsPromise;
  let predictiveSearchScriptPromise;
  let newsletterPopupScriptPromise;
  let euLocalizationPopupScriptPromise;
  let chaportLoaderPromise;
  let freeShippingAssetsPromise;
  let cardFeaturesScriptPromise;
  let productRuntimePromise;
  let freeShippingDrawerObserver;
  let stopWishlistViewportLoader;
  let stopCardFeaturesViewportLoader;

  const runWhenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 1500 });
    } else {
      window.setTimeout(callback, 250);
    }
  };

  const loadStylesheet = (id, href) => {
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    link.media = 'print';
    link.onload = () => {
      link.media = 'all';
      link.onload = null;
    };
    document.head.appendChild(link);
  };

  const loadScript = (id, src) => {
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.defer = true;
    document.head.appendChild(script);
  };

  const loadScriptDeferred = (id, src) =>
    new Promise((resolve, reject) => {
      const existing = document.getElementById(id);

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve(existing);
          return;
        }

        existing.addEventListener('load', () => resolve(existing), { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.defer = true;
      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve(script);
        },
        { once: true }
      );
      script.addEventListener('error', (event) => {
        script.remove();
        reject(event);
      }, { once: true });
      document.head.appendChild(script);
    });

  const scheduleAfterLoad = (callback) => {
    if (document.readyState === 'complete') {
      runWhenIdle(callback);
      return;
    }

    window.addEventListener(
      'load',
      () => {
        runWhenIdle(callback);
      },
      { once: true }
    );
  };

  const teardownCardFeaturesLoader = () => {
    stopCardFeaturesViewportLoader?.();
    document.removeEventListener('pointerdown', handleCardFeaturesWarmup, true);
    document.removeEventListener('mouseover', handleCardFeaturesWarmup, true);
    document.removeEventListener('focusin', handleCardFeaturesWarmup, true);
    document.removeEventListener('click', handleCardFeaturesClick, true);
    document.removeEventListener('keydown', handleCardFeaturesKeydown, true);
  };

  const observeFeatureViewport = (selector, loadFeature) => {
    if (!('IntersectionObserver' in window)) return;

    const observed = new WeakSet();
    const viewportObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadFeature().catch(() => {});
      }
    }, { rootMargin: '200px 0px' });

    const observeControls = (root) => {
      const controls = Array.from(root.querySelectorAll(selector));
      if (root instanceof Element && root.matches(selector)) controls.push(root);

      controls.forEach((control) => {
        const target = control.closest('.card-wrapper, .card-product-wrapper') || control;
        if (observed.has(target)) return;
        observed.add(target);
        viewportObserver.observe(target);
      });
    };

    const domObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) observeControls(node);
        });
      });
    });

    observeControls(document);
    domObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      viewportObserver.disconnect();
      domObserver.disconnect();
    };
  };

  const getStoredExpiration = (key) => {
    try {
      const value = parseInt(localStorage.getItem(key), 10);
      return Number.isFinite(value) ? value : 0;
    } catch (error) {
      return 0;
    }
  };

  const shouldKeepFeatureClosed = (key) => Date.now() <= getStoredExpiration(key);

  const hasLinkableChatText = (value = '') => {
    const normalizedValue = value.toLowerCase();
    return normalizedValue.includes('chat')
      || normalizedValue.includes('vestlusaken')
      || normalizedValue.includes('vestlusaknas');
  };

  const loadCardFeaturesScript = () => {
    if (
      customElements.get('quick-view')
      && customElements.get('card-product-swatch')
      && customElements.get('card-product-sizes')
    ) {
      teardownCardFeaturesLoader();
      return Promise.resolve();
    }

    if (cardFeaturesScriptPromise) return cardFeaturesScriptPromise;

    cardFeaturesScriptPromise = loadScriptDeferred(
      'card-features-script',
      assets.cardFeaturesScript
    )
      .then(() =>
        Promise.all([
          customElements.whenDefined('quick-view'),
          customElements.whenDefined('card-product-swatch'),
          customElements.whenDefined('card-product-sizes')
        ])
      )
      .then(() => {
        teardownCardFeaturesLoader();
      })
      .catch((error) => {
        cardFeaturesScriptPromise = null;
        throw error;
      });

    return cardFeaturesScriptPromise;
  };

  const shouldHandleCardFeatureInteraction = (target) =>
    target instanceof Element && Boolean(target.closest(CARD_FEATURE_INTERACTION_SELECTOR));

  const loadEULocalizationPopupScript = () => {
    if (euLocalizationPopupScriptPromise) return euLocalizationPopupScriptPromise;

    euLocalizationPopupScriptPromise = loadScriptDeferred(
      'eu-localization-popup-script',
      assets.euLocalizationPopupScript
    ).catch((error) => {
      euLocalizationPopupScriptPromise = null;
      throw error;
    });

    return euLocalizationPopupScriptPromise;
  };

  const replayCardFeatureClick = (target) => {
    if (!(target instanceof Element) || !target.isConnected) return;

    window.requestAnimationFrame(() => {
      target.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
    });
  };

  const replayCardFeatureKeydown = (target, key) => {
    if (!(target instanceof Element) || !target.isConnected) return;

    window.requestAnimationFrame(() => {
      target.dispatchEvent(
        new KeyboardEvent('keydown', {
          key,
          bubbles: true,
          cancelable: true
        })
      );
    });
  };

  const replaceChatTextWithLinks = (textNode) => {
    const parentElement = textNode.parentElement;
    const textContent = textNode.textContent;

    if (!parentElement || !textContent || parentElement.closest(CHAT_LINK_SKIP_SELECTOR) || !hasLinkableChatText(textContent)) {
      return;
    }

    CHAT_LINK_PATTERN.lastIndex = 0;

    if (!CHAT_LINK_PATTERN.test(textContent)) {
      return;
    }

    CHAT_LINK_PATTERN.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    textContent.replace(CHAT_LINK_PATTERN, (match, prefix, label, offset) => {
      const prefixOffset = offset;
      const labelOffset = offset + prefix.length;

      if (prefixOffset > lastIndex) {
        fragment.appendChild(document.createTextNode(textContent.slice(lastIndex, prefixOffset)));
      }

      if (prefix) {
        fragment.appendChild(document.createTextNode(prefix));
      }

      const link = document.createElement('a');
      link.href = '#chat';
      link.dataset.openChat = '';
      link.textContent = label;
      fragment.appendChild(link);

      lastIndex = labelOffset + label.length;
      return match;
    });

    if (lastIndex < textContent.length) {
      fragment.appendChild(document.createTextNode(textContent.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  };

  const handleCardFeaturesWarmup = (event) => {
    const target = event.target;
    const card = HOMEPAGE_FEATURES_ON_DEMAND && target instanceof Element
      ? target.closest('.card-wrapper, .card-product-wrapper')
      : null;
    if (!shouldHandleCardFeatureInteraction(target) && !card?.querySelector(CARD_FEATURE_ROOT_SELECTOR)) return;

    loadCardFeaturesScript()
      .then(() => {
        if (card?.isConnected) {
          card.querySelectorAll('card-product-sizes').forEach((sizes) => sizes.ensureMounted());
        }
      })
      .catch(() => {});
  };

  const handleCardFeaturesClick = (event) => {
    if (customElements.get('quick-view')) return;
    if (!shouldHandleCardFeatureInteraction(event.target)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    loadCardFeaturesScript()
      .then(() => {
        replayCardFeatureClick(event.target);
      })
      .catch(() => {});
  };

  const handleCardFeaturesKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (customElements.get('quick-view')) return;
    if (!shouldHandleCardFeatureInteraction(event.target)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    loadCardFeaturesScript()
      .then(() => {
        replayCardFeatureKeydown(event.target, event.key);
      })
      .catch(() => {});
  };

  const linkifyChatText = (root = document.body) => {
    if (CHAT_WIDGET === 'none') return;
    if (!(root instanceof Element) && root !== document.body) return;
    if (!root || !hasLinkableChatText(root.textContent || '')) return;

    const textNodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parentElement = node.parentElement;

        if (!parentElement || parentElement.closest(CHAT_LINK_SKIP_SELECTOR)) {
          return NodeFilter.FILTER_REJECT;
        }

        return hasLinkableChatText(node.textContent || '')
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach(replaceChatTextWithLinks);
  };

  const teardownPredictiveSearchLoader = () => {
    document.removeEventListener('pointerdown', handlePredictiveSearchWarmup, true);
    document.removeEventListener('focusin', handlePredictiveSearchWarmup, true);
    document.removeEventListener('keydown', handlePredictiveSearchKeydown, true);
    document.removeEventListener('input', handlePredictiveSearchInput, true);
  };

  const syncPredictiveSearchInputs = () => {
    document.querySelectorAll('#drawer-search predictive-search input[type="search"]').forEach((input) => {
      if (input.value.trim().length) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  };

  const loadPredictiveSearchScript = () => {
    if (!document.querySelector('#drawer-search predictive-search')) return Promise.resolve();

    loadStylesheet(
      'predictive-search-stylesheet',
      assets.componentPredictiveSearchStylesheet
    );

    if (customElements.get('predictive-search')) return Promise.resolve();
    if (predictiveSearchScriptPromise) return predictiveSearchScriptPromise;

    predictiveSearchScriptPromise = loadScriptDeferred(
      'predictive-search-script',
      assets.predictiveSearchScript
    )
      .then(() => customElements.whenDefined('predictive-search'))
      .then(() => {
        teardownPredictiveSearchLoader();
        syncPredictiveSearchInputs();
      })
      .catch((error) => {
        predictiveSearchScriptPromise = null;
        throw error;
      });

    return predictiveSearchScriptPromise;
  };

  const handlePredictiveSearchWarmup = (event) => {
    const trigger = event.target.closest ? event.target.closest(PREDICTIVE_SEARCH_TRIGGER_SELECTOR) : null;

    if (!trigger) return;

    loadPredictiveSearchScript().catch((error) => {
      console.error('Failed to load predictive search script:', error);
    });
  };

  const handlePredictiveSearchKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    handlePredictiveSearchWarmup(event);
  };

  const handlePredictiveSearchInput = (event) => {
    const input = event.target.closest ? event.target.closest('#drawer-search input[type="search"]') : null;

    if (!input || customElements.get('predictive-search')) return;

    loadPredictiveSearchScript()
      .catch((error) => {
        console.error('Failed to initialize predictive search after input:', error);
      });
  };

  const teardownNewsletterPopupLoader = () => {
    document.removeEventListener('pointerdown', handleNewsletterPopupWarmup, true);
    document.removeEventListener('focusin', handleNewsletterPopupWarmup, true);
    document.removeEventListener('click', handleNewsletterPopupClick, true);
  };

  const dispatchNewsletterPopupRequest = (eventName, configId, automatic = false) => {
    if (!configId) return;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          configId,
          automatic
        }
      })
    );
  };

  const loadNewsletterPopupScript = () => {
    if (customElements.get('newsletter-popup')) return Promise.resolve();
    if (newsletterPopupScriptPromise) return newsletterPopupScriptPromise;

    newsletterPopupScriptPromise = loadScriptDeferred(
      'newsletter-popup-script',
      assets.newsletterPopupScript
    )
      .then(() => {
        if (customElements.get('newsletter-popup')) return Promise.resolve();
        return customElements.whenDefined('newsletter-popup');
      })
      .then(() => {
        teardownNewsletterPopupLoader();
      })
      .catch((error) => {
        newsletterPopupScriptPromise = null;
        throw error;
      });

    return newsletterPopupScriptPromise;
  };

  const getNewsletterPopupConfigId = (target) => {
    if (!(target instanceof Element)) return null;

    const opener = target.closest('.newsletter-modal__opener');
    return opener?.dataset.newsletterTemplateId || null;
  };

  const handleNewsletterPopupWarmup = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !target.closest(NEWSLETTER_POPUP_INTERACTION_SELECTOR)) return;

    loadNewsletterPopupScript().catch(() => {});
  };

  const handleNewsletterPopupClick = (event) => {
    if (customElements.get('newsletter-popup')) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const openerButton = target.closest(NEWSLETTER_POPUP_TRIGGER_SELECTOR);
    const openerClose = target.closest(NEWSLETTER_POPUP_CLOSE_SELECTOR);
    const trigger = openerButton || openerClose;

    if (!trigger || trigger.dataset.newsletterLoading === 'true') return;

    const configId = getNewsletterPopupConfigId(trigger);
    if (!configId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    trigger.dataset.newsletterLoading = 'true';

    loadNewsletterPopupScript()
      .then(() => {
        trigger.removeAttribute('data-newsletter-loading');
        dispatchNewsletterPopupRequest(
          openerClose
            ? 'mymara:newsletter-popup:close-opener-request'
            : 'mymara:newsletter-popup:open-request',
          configId
        );
      })
      .catch(() => {
        trigger.removeAttribute('data-newsletter-loading');
      });
  };

  const initializeNewsletterPopupLoader = () => {
    const newsletterPopupConfigs = [...document.querySelectorAll(NEWSLETTER_POPUP_CONFIG_SELECTOR)];
    if (!newsletterPopupConfigs.length) return;

    document.addEventListener('click', handleNewsletterPopupClick, { capture: true });

    if (document.querySelector(NEWSLETTER_POPUP_INTERACTION_SELECTOR)) {
      document.addEventListener('pointerdown', handleNewsletterPopupWarmup, { capture: true, passive: true });
      document.addEventListener('focusin', handleNewsletterPopupWarmup, { capture: true });
    }

    newsletterPopupConfigs.forEach((config) => {
      if (config.dataset.inlinePayloadId || config.dataset.openInEditor === 'true') {
        scheduleAfterLoad(() => {
          loadNewsletterPopupScript().catch(() => {});
        });
        return;
      }

      if (shouldKeepFeatureClosed(NEWSLETTER_POPUP_CLOSED_KEY)) return;

      const openDelay = parseInt(config.dataset.delay, 10) * 1000 || 2000;

      window.setTimeout(() => {
        loadNewsletterPopupScript()
          .then(() => {
            dispatchNewsletterPopupRequest('mymara:newsletter-popup:open-request', config.id, true);
          })
          .catch(() => {});
      }, openDelay);
    });
  };

  const teardownChaportLoader = () => {
    document.removeEventListener('pointerdown', handleChaportWarmup, true);
    document.removeEventListener('focusin', handleChaportWarmup, true);
    document.removeEventListener('click', handleChaportClick, true);
  };

  const teardownFreeShippingLoader = () => {
    document.removeEventListener('pointerdown', handleFreeShippingWarmup, true);
    document.removeEventListener('focusin', handleFreeShippingWarmup, true);
    document.removeEventListener('cartUpdated', handleFreeShippingDrawerMutation, true);

    if (freeShippingDrawerObserver) {
      freeShippingDrawerObserver.disconnect();
      freeShippingDrawerObserver = null;
    }
  };

  const loadFreeShippingAssets = () => {
    if (!document.querySelector(FREE_SHIPPING_SELECTOR)) return Promise.resolve();

    loadStylesheet('free-shipping-stylesheet', assets.freeShippingStylesheet);

    if (customElements.get('free-shipping-progress-bar')) {
      teardownFreeShippingLoader();
      return Promise.resolve();
    }

    if (freeShippingAssetsPromise) return freeShippingAssetsPromise;

    freeShippingAssetsPromise = loadScriptDeferred(
      'free-shipping-script',
      assets.freeShippingScript
    )
      .then(() => customElements.whenDefined('free-shipping-progress-bar'))
      .then(() => {
        teardownFreeShippingLoader();
      })
      .catch((error) => {
        freeShippingAssetsPromise = null;
        throw error;
      });

    return freeShippingAssetsPromise;
  };

  const handleFreeShippingDrawerMutation = () => {
    const cartDrawer = document.querySelector(FREE_SHIPPING_DRAWER_SELECTOR);

    if (
      !cartDrawer
      || !cartDrawer.classList.contains('active')
      || !cartDrawer.querySelector(FREE_SHIPPING_SELECTOR)
    ) {
      return;
    }

    loadFreeShippingAssets().catch(() => {});
  };

  const handleFreeShippingWarmup = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !target.closest(FREE_SHIPPING_DRAWER_TRIGGER_SELECTOR)) return;

    const cartDrawer = document.querySelector(FREE_SHIPPING_DRAWER_SELECTOR);
    if (!cartDrawer || !cartDrawer.querySelector(FREE_SHIPPING_SELECTOR)) return;

    loadFreeShippingAssets().catch(() => {});
  };

  const initializeFreeShippingLoader = () => {
    const isCartPage = document.body.classList.contains('template-cart');
    if (isCartPage) return;

    const cartDrawer = document.querySelector(FREE_SHIPPING_DRAWER_SELECTOR);
    if (!cartDrawer) return;

    document.addEventListener('pointerdown', handleFreeShippingWarmup, { capture: true, passive: true });
    document.addEventListener('focusin', handleFreeShippingWarmup, { capture: true });
    document.addEventListener('cartUpdated', handleFreeShippingDrawerMutation, { capture: true });

    freeShippingDrawerObserver = new MutationObserver(handleFreeShippingDrawerMutation);
    freeShippingDrawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });

    handleFreeShippingDrawerMutation();
  };

  const loadChaportLoaderScript = () => {
    if (window.MyMaraChat && typeof window.MyMaraChat.open === 'function') {
      return Promise.resolve(window.MyMaraChat);
    }

    if (chaportLoaderPromise) return chaportLoaderPromise;

    chaportLoaderPromise = loadScriptDeferred('chaport-loader-script', assets.chaportLoaderScript)
      .then(() => {
        teardownChaportLoader();
        return window.MyMaraChat;
      })
      .catch((error) => {
        chaportLoaderPromise = null;
        throw error;
      });

    return chaportLoaderPromise;
  };

  const handleChaportWarmup = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !target.closest(CHAPORT_TRIGGER_SELECTOR)) return;

    loadChaportLoaderScript().catch(() => {});
  };

  const handleChaportClick = (event) => {
    if (window.MyMaraChat && typeof window.MyMaraChat.open === 'function') return;

    const target = event.target;

    if (!(target instanceof Element)) return;

    const trigger = target.closest(CHAPORT_TRIGGER_SELECTOR);

    if (!trigger || trigger.dataset.chatLoading === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    trigger.dataset.chatLoading = 'true';

    loadChaportLoaderScript()
      .then(() => {
        trigger.removeAttribute('data-chat-loading');

        if (window.MyMaraChat && typeof window.MyMaraChat.open === 'function') {
          window.MyMaraChat.open();
          return;
        }

        window.requestAnimationFrame(() => {
          trigger.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );
        });
      })
      .catch(() => {
        trigger.removeAttribute('data-chat-loading');
      });
  };

  const hasChatHash = () => window.location.hash.toLowerCase() === '#chat';

  const openChaportFromHash = () => {
    if (!hasChatHash()) return;

    loadChaportLoaderScript()
      .then(() => {
        if (window.MyMaraChat && typeof window.MyMaraChat.open === 'function') {
          window.MyMaraChat.open();
        }
      })
      .catch(() => {});
  };

  // MyMara Chat: the widget script is in the page (async); a click made before it has run is
  // queued until it announces mymara-chat:ready, so no click is lost.
  let myMaraChatOpenQueued = false;

  const openMyMaraChat = () => {
    const api = window.MyMaraChat;

    if (api && typeof api.open === 'function') {
      api.open();
      return;
    }

    if (myMaraChatOpenQueued) return;
    myMaraChatOpenQueued = true;

    document.addEventListener(MYMARA_CHAT_READY_EVENT, () => {
      myMaraChatOpenQueued = false;
      document.dispatchEvent(new CustomEvent(MYMARA_CHAT_OPEN_EVENT));
    }, { once: true });
  };

  const handleMyMaraChatClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !target.closest(CHAPORT_TRIGGER_SELECTOR)) return;

    event.preventDefault();
    openMyMaraChat();
  };

  const openMyMaraChatFromHash = () => {
    if (hasChatHash()) openMyMaraChat();
  };

  const initializeMyMaraChat = () => {
    document.addEventListener('click', handleMyMaraChatClick, { capture: true });
    window.addEventListener('hashchange', openMyMaraChatFromHash);
    openMyMaraChatFromHash();
  };

  // Chat off: chat links are plain text (snippets/mymara-chat.liquid styles); keep them from adding #chat.
  const initializeChatOff = () => {
    document.addEventListener('click', (event) => {
      const target = event.target;

      if (target instanceof Element && target.closest(CHAPORT_TRIGGER_SELECTOR)) event.preventDefault();
    }, { capture: true });
  };

  const teardownWishlistLoader = () => {
    stopWishlistViewportLoader?.();
    document.removeEventListener('pointerdown', handleWishlistWarmup, true);
    document.removeEventListener('mouseover', handleWishlistWarmup, true);
    document.removeEventListener('focusin', handleWishlistWarmup, true);
    document.removeEventListener('click', handleWishlistClick, true);
  };

  const loadWishlistScripts = () => {
    const loadPage = () => document.querySelector(WISHLIST_PAGE_SELECTOR)
      ? loadScriptDeferred('wishlist-page-script', assets.wishlistPageScript)
      : Promise.resolve();
    if (window.MymaraWishlist) {
      teardownWishlistLoader();
      return loadPage().then(() => window.MymaraWishlist);
    }
    if (wishlistScriptsPromise) return wishlistScriptsPromise;

    const hasSharedWishlist = new URLSearchParams(window.location.search).has('share');
    const shouldLoadWishlistSync = Boolean(
      (window.MymaraWishlistConfig && window.MymaraWishlistConfig.customerLoggedIn) ||
        hasSharedWishlist
    );

    wishlistScriptsPromise = (shouldLoadWishlistSync
      ? loadScriptDeferred('wishlist-sync-script', assets.wishlistSyncScript)
      : Promise.resolve()
    )
      .then(() => loadScriptDeferred('wishlist-script', assets.wishlistScript))
      .then(() => loadPage())
      .then(() => {
        teardownWishlistLoader();
        return window.MymaraWishlist;
      })
      .catch((error) => {
        wishlistScriptsPromise = null;
        throw error;
      });

    return wishlistScriptsPromise;
  };

  const handleWishlistWarmup = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !target.closest(WISHLIST_TRIGGER_SELECTOR)) return;

    loadWishlistScripts().catch(() => {});
  };

  const handleWishlistClick = (event) => {
    if (window.MymaraWishlist) return;

    const target = event.target;

    if (!(target instanceof Element)) return;

    const button = target.closest(WISHLIST_BUTTON_SELECTOR);

    if (!button || button.dataset.wishlistLoading === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    button.dataset.wishlistLoading = 'true';

    loadWishlistScripts()
      .then(() => {
        button.removeAttribute('data-wishlist-loading');

        window.requestAnimationFrame(() => {
          button.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );
        });
      })
      .catch(() => {
        button.removeAttribute('data-wishlist-loading');
      });
  };

  const initializeWishlistLoader = () => {
    if (document.querySelector(WISHLIST_PAGE_SELECTOR)) {
      loadWishlistScripts().catch(() => {});
      return;
    }

    document.addEventListener('click', handleWishlistClick, { capture: true });

    document.addEventListener('mouseover', handleWishlistWarmup, { capture: true, passive: true });
    document.addEventListener('focusin', handleWishlistWarmup, { capture: true });

    if (window.MymaraWishlistConfig && window.MymaraWishlistConfig.customerLoggedIn) {
      scheduleAfterLoad(() => {
        loadWishlistScripts().catch(() => {});
      });
      return;
    }

    if (HOMEPAGE_FEATURES_ON_DEMAND) {
      document.addEventListener('pointerdown', handleWishlistWarmup, { capture: true, passive: true });
      stopWishlistViewportLoader = observeFeatureViewport(WISHLIST_BUTTON_SELECTOR, loadWishlistScripts);
      return;
    }

    scheduleAfterLoad(() => {
      loadWishlistScripts().catch(() => {});
    });
  };

  const initializeChaportLoader = () => {
    document.addEventListener('click', handleChaportClick, { capture: true });
    window.addEventListener('hashchange', openChaportFromHash);

    if (document.querySelector(CHAPORT_TRIGGER_SELECTOR)) {
      document.addEventListener('pointerdown', handleChaportWarmup, { capture: true, passive: true });
      document.addEventListener('focusin', handleChaportWarmup, { capture: true });
    }

    openChaportFromHash();
    scheduleChaportLoad();
  };

  // Chat loads on the visitor's first scroll, tap or key press, or a while after load if there is none,
  // so its scripts and long tasks stay out of the page's initial load
  const scheduleChaportLoad = () => {
    let fallbackTimer = null;

    const load = () => {
      CHAPORT_INTERACTION_EVENTS.forEach(([type, capture]) => window.removeEventListener(type, load, capture));
      window.removeEventListener('load', startFallback);
      window.clearTimeout(fallbackTimer);
      runWhenIdle(() => {
        loadChaportLoaderScript().catch(() => {});
      });
    };

    const startFallback = () => {
      fallbackTimer = window.setTimeout(load, CHAPORT_FALLBACK_DELAY);
    };

    CHAPORT_INTERACTION_EVENTS.forEach(([type, capture]) => {
      window.addEventListener(type, load, { capture, passive: true, once: true });
    });

    if (document.readyState === 'complete') {
      startFallback();
    } else {
      window.addEventListener('load', startFallback, { once: true });
    }
  };

  const initializeCardFeaturesLoader = () => {
    if (!HOMEPAGE_FEATURES_ON_DEMAND && !document.querySelector(CARD_FEATURE_ROOT_SELECTOR)) return;

    document.addEventListener('click', handleCardFeaturesClick, { capture: true });
    document.addEventListener('keydown', handleCardFeaturesKeydown, { capture: true });
    document.addEventListener('pointerdown', handleCardFeaturesWarmup, { capture: true, passive: true });
    document.addEventListener('mouseover', handleCardFeaturesWarmup, { capture: true, passive: true });
    document.addEventListener('focusin', handleCardFeaturesWarmup, { capture: true });

    if (HOMEPAGE_FEATURES_ON_DEMAND) {
      stopCardFeaturesViewportLoader = observeFeatureViewport(CARD_FEATURE_ROOT_SELECTOR, loadCardFeaturesScript);
      return;
    }

    scheduleAfterLoad(() => {
      loadCardFeaturesScript().catch(() => {});
    });
  };

  const initializeEULocalizationPopupLoader = (root = document) => {
    if (!root.querySelector(EU_LOCALIZATION_POPUP_SELECTOR)) return;

    scheduleAfterLoad(() => {
      loadEULocalizationPopupScript().catch(() => {});
    });
  };

  const initializeProductRuntime = (root) => {
    if (!root.querySelector?.(PRODUCT_RUNTIME_SELECTOR) && !root.matches?.(PRODUCT_RUNTIME_SELECTOR)) return Promise.resolve();
    if (customElements.get('variant-selects') && customElements.get('mymara-size-guide')) return Promise.resolve();
    productRuntimePromise ||= loadScriptDeferred('product-runtime-script', assets.productRuntimeScript)
      .catch((error) => { productRuntimePromise = null; throw error; });
    productRuntimePromise.catch(() => {});
    return productRuntimePromise;
  };

  const replayProductInteraction = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const host = target.closest(PRODUCT_RUNTIME_SELECTOR);
    if (!host || customElements.get(host.localName)) return;
    if (event.type === 'click' && !target.closest('label, button, input[type="radio"]')) return;
    if (event.type === 'click') event.preventDefault();
    event.stopImmediatePropagation();
    initializeProductRuntime(host).then(() => {
      if (!target.isConnected) return;
      if (event.type === 'click') target.click();
      else target.dispatchEvent(new Event('change', { bubbles: true }));
    }).catch(() => {});
  };

  document.addEventListener('click', replayProductInteraction, true);
  document.addEventListener('change', replayProductInteraction, true);

  const loadFeatureScripts = () => {
    scheduleAfterLoad(() => {
      linkifyChatText();
    });

    if (document.querySelector('quick-view') || document.body.classList.contains('template-product-quick-view')) {
      loadStylesheet('quick-view-stylesheet', assets.quickViewStylesheet);
    }

    initializeCardFeaturesLoader();

    if (document.querySelector('#drawer-search predictive-search')) {
      document.addEventListener('pointerdown', handlePredictiveSearchWarmup, true);
      document.addEventListener('focusin', handlePredictiveSearchWarmup, true);
      document.addEventListener('keydown', handlePredictiveSearchKeydown, true);
      document.addEventListener('input', handlePredictiveSearchInput, true);
    }

    if (CHAT_WIDGET === 'mymara') {
      initializeMyMaraChat();
    } else if (CHAT_WIDGET === 'none') {
      initializeChatOff();
    } else {
      initializeChaportLoader();
    }

    initializeNewsletterPopupLoader();

    initializeEULocalizationPopupLoader();

    if (
      document.querySelector('.newsletter-modal__opener')
      && !document.body.classList.contains('template-page-contact')
      && !document.body.classList.contains('template-index')
    ) {
      runWhenIdle(() => {
        loadScript('newsletter-scroll-script', assets.newsletterScrollScript);
      });
    }

    if (document.querySelector('.megamenu-overlay') && document.querySelector('li.list-menu__has-mega')) {
      loadScript('megamenu-overlay-script', assets.megamenuOverlayScript);
    }

    if (config.showFreeShippingBar) {
      initializeFreeShippingLoader();
    }

    initializeWishlistLoader();
    initializeProductRuntime(document);
  };


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadFeatureScripts, { once: true });
  } else {
    loadFeatureScripts();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
      runWhenIdle(() => {
        linkifyChatText(event.target);
        initializeEULocalizationPopupLoader(event.target);
        initializeProductRuntime(event.target);
      });
    }
  });
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        initializeProductRuntime(node);
        if (node.matches(WISHLIST_PAGE_SELECTOR) || node.querySelector(WISHLIST_PAGE_SELECTOR)) {
          loadWishlistScripts().catch(() => {});
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
